import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";

import {
  AGGREGATION_CALCULATION_OPTIONS,
  AGGREGATION_CALCULATION_PIVOT_OPTIONS,
  CATEGORY_OPTIONS,
  METHOD_OPTIONS,
  SORT_BY_OPTIONS
} from "./constants";
import {
  AnalysisConfiguration,
  ECategoryTransform,
  TransformationMethod
} from "./types";

type TransformationMethodSectionProps = {
  attributesOptions?: IOptionItem[];
  analysisConfiguration: AnalysisConfiguration | null;
  setAnalysisConfiguration: (method: AnalysisConfiguration | null) => void;
};

const TransformationMethodSection = ({
  attributesOptions = [],
  analysisConfiguration,
  setAnalysisConfiguration
}: TransformationMethodSectionProps) => {
  const { gridValue } = useAnalysisSummaryDataContext();

  const isPivotMethod =
    analysisConfiguration?.method?.value === TransformationMethod.PIVOT;

  const handleSelectAttribute = (
    option: IOptionItem | null,
    axis: "X" | "Y"
  ) => {
    setAnalysisConfiguration({
      ...analysisConfiguration,
      filterBy: ["all"],
      attribute_x: axis === "X" ? option : analysisConfiguration?.attribute_x,
      attribute_y: axis === "Y" ? option : analysisConfiguration?.attribute_y
    });
  };

  const handleSelectMethod = (option: IOptionItem | null) => {
    const isPivot = option?.value === TransformationMethod.PIVOT;

    setAnalysisConfiguration({
      ...analysisConfiguration,
      sortBy: !isPivot
        ? { value: gridValue[0].value, label: gridValue[0].value }
        : analysisConfiguration?.sortBy,
      category: isPivot ? CATEGORY_OPTIONS[1] : CATEGORY_OPTIONS[0],
      method: option, // If the method is PIVOT, set the default calculation method to COUNT
      calculationMethod: isPivot
        ? AGGREGATION_CALCULATION_PIVOT_OPTIONS[0]
        : analysisConfiguration?.calculationMethod,
      // If the method is PIVOT, set the attribute to the first attribute
      attribute_x: isPivot ? attributesOptions?.[0] : null,
      attribute_y: isPivot ? attributesOptions?.[0] : null,
      filterBy: ["all"]
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <Dropdown
        className="w-full mt-2"
        name="method"
        placeholder="Method"
        data={METHOD_OPTIONS}
        onSelect={handleSelectMethod}
        value={analysisConfiguration?.method}
      />
      {isPivotMethod ? (
        <>
          <Dropdown
            className="w-full mt-2"
            name="attribute_x"
            placeholder="Attribute X axis"
            data={attributesOptions}
            onSelect={(option) => handleSelectAttribute(option, "X")}
            value={analysisConfiguration?.attribute_x}
          />
          <Dropdown
            className="w-full mt-2"
            name="attribute_y"
            placeholder="Attribute Y axis"
            data={attributesOptions}
            onSelect={(option) => handleSelectAttribute(option, "Y")}
            value={analysisConfiguration?.attribute_y}
          />
        </>
      ) : (
        <Dropdown
          className="w-full mt-2"
          name="category"
          placeholder="Category"
          data={CATEGORY_OPTIONS}
          onSelect={(option) => {
            setAnalysisConfiguration({
              ...analysisConfiguration,
              sortBy:
                option?.value === ECategoryTransform.DISPLAY_ROWS_AGG
                  ? SORT_BY_OPTIONS[0]
                  : { value: gridValue[0].value, label: gridValue[0].value },
              category: option
            });
          }}
          value={analysisConfiguration?.category}
        />
      )}
      <Dropdown
        className="w-full mt-2"
        name="aggregationCalculation"
        placeholder="Agg.Calculation"
        data={AGGREGATION_CALCULATION_OPTIONS}
        onSelect={(option) =>
          setAnalysisConfiguration({
            ...analysisConfiguration,
            calculationMethod: option
          })
        }
        value={analysisConfiguration?.calculationMethod}
      />
    </div>
  );
};

export default TransformationMethodSection;
